import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Users, Bell, CheckCircle, Clock } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const FrontlineWorkerDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [dashRes, alertsRes] = await Promise.all([
        api.getFrontlineDashboard(),
        api.getAlerts()
      ]);
      setDashboard(dashRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertAction = async (alertId, status) => {
    try {
      await api.updateAlertStatus(alertId, status);
      fetchDashboard();
    } catch (error) {
      console.error('Error updating alert:', error);
    }
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return 'bg-red-100 text-red-800 border-red-300';
    if (priority === 'medium') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="frontline-dashboard">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="dashboard-title">
            {t('frontline.fieldDashboard')}
          </h1>
          <p className="text-lg text-[#4B5563]">
            {t('frontline.monitorCases')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="stat-total-children">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-[#3B82F6]" size={32} />
              <span className="text-3xl font-bold text-[#111827] font-['Nunito']">{dashboard?.total_children || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.totalChildren')}</p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-red-200 bg-red-50" data-testid="stat-high-risk">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-[#EF4444]" size={32} />
              <span className="text-3xl font-bold text-[#EF4444] font-['Nunito']">{dashboard?.high_risk_cases || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#991B1B] uppercase tracking-wider">{t('dashboard.highRisk')}</p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="stat-pending-alerts">
            <div className="flex items-center justify-between mb-2">
              <Bell className="text-[#F59E0B]" size={32} />
              <span className="text-3xl font-bold text-[#111827] font-['Nunito']">{dashboard?.pending_alerts || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.alerts')}</p>
          </div>
        </div>

        {/* Alerts Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-[#111827] mb-6 font-['Nunito']" data-testid="alerts-section-title">{t('frontline.priorityAlerts')}</h2>

          {alerts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border-2 border-slate-100" data-testid="no-alerts-message">
              <CheckCircle className="mx-auto text-[#10B981] mb-4" size={64} />
              <p className="text-lg text-[#4B5563]">{t('frontline.noAlerts')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  data-testid={`alert-card-${alert.id}`}
                  className="rounded-2xl border-2 border-slate-100 bg-white p-6 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between">
                    <div className="flex-1 mb-4 md:mb-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`text-xs px-3 py-1 rounded-full font-bold border-2 ${getPriorityColor(alert.priority)}`}>
                          {alert.priority.toUpperCase()}
                        </span>
                        <span className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full font-semibold">
                          {alert.type.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        {alert.status === 'pending' && (
                          <Clock className="text-[#F59E0B]" size={18} />
                        )}
                      </div>
                      <p className="text-lg font-semibold text-[#111827] mb-1">{alert.message}</p>
                      <p className="text-sm text-[#4B5563]">{t('frontline.created')}: {new Date(alert.created_at).toLocaleDateString()}</p>
                    </div>
                    
                    {alert.status === 'pending' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleAlertAction(alert.id, 'in_progress')}
                          data-testid={`alert-action-progress-${alert.id}`}
                          className="px-6 py-3 bg-[#3B82F6] text-white rounded-xl font-bold hover:bg-[#2563EB] transition-all active:scale-95"
                        >
                          {t('frontline.start')}
                        </button>
                        <button
                          onClick={() => handleAlertAction(alert.id, 'resolved')}
                          data-testid={`alert-action-resolve-${alert.id}`}
                          className="px-6 py-3 bg-[#10B981] text-white rounded-xl font-bold hover:bg-[#059669] transition-all active:scale-95"
                        >
                          {t('frontline.resolve')}
                        </button>
                      </div>
                    )}
                    
                    {alert.status === 'in_progress' && (
                      <button
                        onClick={() => handleAlertAction(alert.id, 'resolved')}
                        data-testid={`alert-resolve-${alert.id}`}
                        className="px-6 py-3 bg-[#10B981] text-white rounded-xl font-bold hover:bg-[#059669] transition-all active:scale-95"
                      >
                        {t('frontline.markResolved')}
                      </button>
                    )}
                    
                    {alert.status === 'resolved' && (
                      <div className="flex items-center space-x-2 text-[#10B981]">
                        <CheckCircle size={24} />
                        <span className="font-bold">{t('frontline.resolved')}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('frontline.quickActions')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/bulk-upload')}
              data-testid="bulk-upload-button"
              className="w-full rounded-xl py-4 text-xl font-bold uppercase tracking-wide shadow-sm border-2 border-[#10B981] bg-[#10B981] text-white hover:bg-[#059669] transition-all active:scale-95"
            >
              {t('bulkUpload.title')}
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="view-all-children-button"
              className="w-full rounded-xl py-4 text-xl font-bold uppercase tracking-wide shadow-sm border-2 border-[#0F4C81] bg-[#0F4C81] text-white hover:bg-[#0d3d6b] transition-all active:scale-95"
            >
              {t('frontline.viewAllChildren')}
            </button>
            <button
              onClick={() => window.location.reload()}
              data-testid="refresh-alerts-button"
              className="w-full rounded-xl py-4 text-xl font-bold uppercase tracking-wide shadow-sm border-2 border-[#F59E0B] bg-[#F59E0B] text-black hover:bg-[#D97706] transition-all active:scale-95"
            >
              {t('frontline.refreshAlerts')}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    {/* Copyright Footer */}
    <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
      <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
    </footer>
    </>
  );
};

export default FrontlineWorkerDashboard;